import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import api from '../utils/apiConfig';
import { useLocation } from './LocationContext';

const OrderContext = createContext();

// Action types
const ORDER_ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  SET_ORDERS: 'SET_ORDERS',
  SET_STATS: 'SET_STATS',
  ADD_ORDER: 'ADD_ORDER',
  UPDATE_ORDER: 'UPDATE_ORDER',
  DELETE_ORDER: 'DELETE_ORDER',
  SET_ERROR: 'SET_ERROR',
  CLEAR_ERROR: 'CLEAR_ERROR',
  SET_FILTERS: 'SET_FILTERS',
  SET_PAGINATION: 'SET_PAGINATION'
};

// Initial state
const initialState = {
  orders: [],
  stats: {
    totalOrders: 0,
    pendingOrders: 0,
    completedOrders: 0,
    totalRevenue: 0,
    averageOrderValue: 0,
    statusBreakdown: [],
    dailyStats: []
  },
  loading: false,
  error: null,
  filters: {
    search: '',
    status: '',
    priority: '',
    orderType: '',
    sortBy: 'createdAt',
    sortOrder: 'desc'
  },
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 20,
    hasNextPage: false,
    hasPrevPage: false
  }
};

// Reducer
const orderReducer = (state, action) => {
  switch (action.type) {
    case ORDER_ACTIONS.SET_LOADING:
      return {
        ...state,
        loading: action.payload
      };
    case ORDER_ACTIONS.SET_ORDERS:
      return {
        ...state,
        orders: action.payload.orders,
        pagination: action.payload.pagination,
        loading: false,
        error: null
      };
    case ORDER_ACTIONS.SET_STATS:
      return {
        ...state,
        stats: action.payload,
        loading: false,
        error: null
      };
    case ORDER_ACTIONS.ADD_ORDER:
      return {
        ...state,
        orders: [action.payload, ...state.orders],
        loading: false,
        error: null
      };
    case ORDER_ACTIONS.UPDATE_ORDER:
      return {
        ...state,
        orders: state.orders.map(order =>
          order.id === action.payload.id ? action.payload : order
        ),
        loading: false,
        error: null
      };
    case ORDER_ACTIONS.DELETE_ORDER:
      return {
        ...state,
        orders: state.orders.filter(order => order.id !== action.payload),
        loading: false,
        error: null
      };
    case ORDER_ACTIONS.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        loading: false
      };
    case ORDER_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null
      };
    case ORDER_ACTIONS.SET_FILTERS:
      return {
        ...state,
        filters: { ...state.filters, ...action.payload }
      };
    case ORDER_ACTIONS.SET_PAGINATION:
      return {
        ...state,
        pagination: { ...state.pagination, ...action.payload }
      };
    default:
      return state;
  }
};

// Provider component
export const OrderProvider = ({ children }) => {
  const [state, dispatch] = useReducer(orderReducer, initialState);
  const { selectedLocation } = useLocation();


  const loadOrders = useCallback(async (page = 1, filters = {}) => {
    if (!selectedLocation?._id) return;

    try {
      dispatch({ type: ORDER_ACTIONS.SET_LOADING, payload: true });
      
      const paramObj = {
        locationId: selectedLocation._id,
        page: page.toString(),
        limit: state.pagination.itemsPerPage.toString(),
        ...state.filters,
        ...filters
      };
      Object.keys(paramObj).forEach((key) => {
        if (paramObj[key] === '' || paramObj[key] === null || paramObj[key] === undefined) {
          delete paramObj[key];
        }
      });
      const params = new URLSearchParams(paramObj);

      const response = await api.get(`/orders?${params}`);
      
      if (response.data.success) {
        dispatch({
          type: ORDER_ACTIONS.SET_ORDERS,
          payload: response.data.data
        });
      }
    } catch (error) {
      console.error('Load orders error:', error);
      dispatch({
        type: ORDER_ACTIONS.SET_ERROR,
        payload: error.response?.data?.message || 'Failed to load orders'
      });
    }
  }, [selectedLocation?._id, state.pagination.itemsPerPage, state.filters]);

  const loadStats = useCallback(async (period = '30d') => {
    if (!selectedLocation?._id) return;

    try {
      const response = await api.get(`/orders/stats/${selectedLocation._id}?period=${period}`);
      
      if (response.data.success) {
        dispatch({
          type: ORDER_ACTIONS.SET_STATS,
          payload: response.data.data.stats
        });
      }
    } catch (error) {
      console.error('Load stats error:', error);
      dispatch({
        type: ORDER_ACTIONS.SET_ERROR,
        payload: error.response?.data?.message || 'Failed to load statistics'
      });
    }
  }, [selectedLocation?._id]);

  const getOrder = useCallback(async (orderId) => {
    try {
      const response = await api.get(`/orders/${orderId}`);
      
      if (response.data.success) {
        return response.data.data.order;
      }
    } catch (error) {
      console.error('Get order error:', error);
      dispatch({
        type: ORDER_ACTIONS.SET_ERROR,
        payload: error.response?.data?.message || 'Failed to get order'
      });
      throw error;
    }
  }, []);

  const addOrder = useCallback(async (orderData) => {
    if (!selectedLocation?._id) return;

    try {
      dispatch({ type: ORDER_ACTIONS.SET_LOADING, payload: true });
      
      const response = await api.post('/orders', {
        ...orderData,
        locationId: selectedLocation._id
      });
      
      if (response.data.success) {
        dispatch({
          type: ORDER_ACTIONS.ADD_ORDER,
          payload: response.data.data.order
        });
        return response.data.data.order;
      }
    } catch (error) {
      console.error('Add order error:', error);
      dispatch({
        type: ORDER_ACTIONS.SET_ERROR,
        payload: error.response?.data?.message || 'Failed to add order'
      });
      throw error;
    }
  }, [selectedLocation?._id]);

  const updateOrder = useCallback(async (orderId, orderData) => {
    try {
      dispatch({ type: ORDER_ACTIONS.SET_LOADING, payload: true });
      
      const response = await api.put(`/orders/${orderId}`, orderData);
      
      if (response.data.success) {
        dispatch({
          type: ORDER_ACTIONS.UPDATE_ORDER,
          payload: response.data.data.order
        });
        return response.data.data.order;
      }
    } catch (error) {
      console.error('Update order error:', error);
      dispatch({
        type: ORDER_ACTIONS.SET_ERROR,
        payload: error.response?.data?.message || 'Failed to update order'
      });
      throw error;
    }
  }, []);

  const deleteOrder = useCallback(async (orderId) => {
    try {
      // No need to set loading here as loadOrders will handle it.
      const response = await api.delete(`/orders/${orderId}`);
      
      if (response.data.success) {
        // Instead of dispatching delete, just reload the orders to get the fresh list
        await loadOrders(state.pagination.currentPage, state.filters);
        // Optionally, add a success toast here
        return true;
      }
    } catch (error) {
      console.error('Delete order error:', error);
      dispatch({
        type: ORDER_ACTIONS.SET_ERROR,
        payload: error.response?.data?.message || 'Failed to delete order'
      });
      // Optionally, add an error toast here
      throw error;
    }
  }, [loadOrders, state.pagination.currentPage, state.filters]);

  const updateFilters = useCallback((newFilters) => {
    // Reset to page 1 when filters change
    dispatch({ type: ORDER_ACTIONS.SET_PAGINATION, payload: { currentPage: 1 } });
    dispatch({ type: ORDER_ACTIONS.SET_FILTERS, payload: newFilters });
  }, []);

  const changePage = useCallback((newPage) => {
    dispatch({ type: ORDER_ACTIONS.SET_PAGINATION, payload: { currentPage: newPage } });
  }, []);

  const clearError = useCallback(() => {
    dispatch({ type: ORDER_ACTIONS.CLEAR_ERROR });
  }, []);

  // Load orders on location, filter, or page change
  // Using refs to prevent duplicate API calls
  const initialLoadRef = React.useRef(false);
  const prevLocationRef = React.useRef(null);
  
  useEffect(() => {
    if (!selectedLocation?._id) {
      initialLoadRef.current = false;
      prevLocationRef.current = null;
      return;
    }
    
    // Only load if location actually changed or this is first load
    const locationChanged = prevLocationRef.current !== selectedLocation._id;
    
    if (locationChanged || !initialLoadRef.current) {
      prevLocationRef.current = selectedLocation._id;
      initialLoadRef.current = true;
      
      // Batch initial loads together
      loadOrders(state.pagination.currentPage, state.filters);
      loadStats();
    }
  }, [selectedLocation?._id]);
  
  // Separate effect for filter/pagination changes (not location changes)
  const filtersRef = React.useRef(JSON.stringify(state.filters));
  const pageRef = React.useRef(state.pagination.currentPage);
  
  useEffect(() => {
    if (!selectedLocation?._id || !initialLoadRef.current) return;
    
    const filtersChanged = filtersRef.current !== JSON.stringify(state.filters);
    const pageChanged = pageRef.current !== state.pagination.currentPage;
    
    if (filtersChanged || pageChanged) {
      filtersRef.current = JSON.stringify(state.filters);
      pageRef.current = state.pagination.currentPage;
      loadOrders(state.pagination.currentPage, state.filters);
    }
  }, [state.filters, state.pagination.currentPage, selectedLocation?._id, loadOrders]);

  const value = {
    // State
    orders: state.orders,
    stats: state.stats,
    loading: state.loading,
    error: state.error,
    filters: state.filters,
    pagination: state.pagination,
    
    // Actions
    loadOrders,
    loadStats,
    getOrder,
    addOrder,
    updateOrder,
    deleteOrder,
    updateFilters,
    changePage,
    clearError
  };

  return (
    <OrderContext.Provider value={value}>
      {children}
    </OrderContext.Provider>
  );
};

// Hook to use order context
export const useOrder = () => {
  const context = useContext(OrderContext);
  if (!context) {
    throw new Error('useOrder must be used within an OrderProvider');
  }
  return context;
};

export default OrderContext;
